import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import axios from "axios";
import { configureAuth } from "@/api/client";
import * as endpoints from "@/api/endpoints";
import type { LoginResponse, User } from "@/api/types";

interface AuthState {
  user: User | null;
  token: string | null;
  status: "initializing" | "authenticated" | "unauthenticated";
}

interface AuthContextValue extends AuthState {
  login: (email: string, password: string) => Promise<LoginResponse>;
  logout: () => Promise<void>;
  refreshMe: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

/** access_token は sessionStorage に保持（タブを閉じるとクリア）。 */
const TOKEN_KEY = "kz.access_token";

function loadStoredToken(): string | null {
  try {
    return sessionStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>(() => ({
    user: null,
    token: loadStoredToken(),
    status: "initializing",
  }));

  // axios クライアントに getter/setter を注入
  const tokenRef = useRef<string | null>(state.token);
  tokenRef.current = state.token;

  const setToken = useCallback((token: string | null) => {
    tokenRef.current = token;
    try {
      if (token) sessionStorage.setItem(TOKEN_KEY, token);
      else sessionStorage.removeItem(TOKEN_KEY);
    } catch {
      // ignore storage errors
    }
    setState((s) => ({ ...s, token }));
  }, []);

  const clearSession = useCallback(() => {
    tokenRef.current = null;
    try {
      sessionStorage.removeItem(TOKEN_KEY);
    } catch {
      // ignore
    }
    setState({ user: null, token: null, status: "unauthenticated" });
  }, []);

  useEffect(() => {
    configureAuth(
      () => tokenRef.current,
      setToken,
      clearSession,
    );
  }, [setToken, clearSession]);

  // 初回: リフレッシュトークン cookie が残っていれば /auth/refresh で復帰を試みる
  useEffect(() => {
    let cancelled = false;
    (async () => {
      // すでに token を保持していれば /me で検証、なければ refresh を試行
      try {
        if (tokenRef.current) {
          const user = await endpoints.me();
          if (!cancelled) {
            setState({ user, token: tokenRef.current, status: "authenticated" });
          }
          return;
        }
        // cookie ベースの refresh を試す
        const { data } = await axios.post<{ access_token: string }>(
          "/api/v1/auth/refresh",
          {},
          { withCredentials: true },
        );
        setToken(data.access_token);
        const user = await endpoints.me();
        if (!cancelled) {
          setState({ user, token: data.access_token, status: "authenticated" });
        }
      } catch {
        if (!cancelled) {
          clearSession();
        }
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = useCallback(
    async (email: string, password: string) => {
      const resp = await endpoints.login(email, password);
      setToken(resp.access_token);
      setState({ user: resp.user, token: resp.access_token, status: "authenticated" });
      return resp;
    },
    [setToken],
  );

  const logout = useCallback(async () => {
    try {
      await endpoints.logout();
    } catch {
      // 失敗しても強制クリア
    }
    clearSession();
  }, [clearSession]);

  const refreshMe = useCallback(async () => {
    const user = await endpoints.me();
    setState((s) => ({ ...s, user, status: "authenticated" }));
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({ ...state, login, logout, refreshMe }),
    [state, login, logout, refreshMe],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
